import { WHITE_LABEL_ID } from "config";
import brandingService from "services/brandingService";

/**
 * Utilidad para cargar y aplicar branding al inicio de la aplicación
 * Uso: import { initializeBranding } from 'utils/brandingInit';
 */

/**
 * Inicializa el branding al arrancar la aplicación
 * @param {number} negocioId - ID del negocio (opcional, legacy)
 * @returns {Promise<Object>} Configuración de branding aplicada
 */
export const initializeBranding = async (negocioId = null) => {
  try {
    // Priorizar WHITE_LABEL_ID de variables de entorno
    const effectiveNegocioId = WHITE_LABEL_ID ? null : negocioId;

    // Cargar y aplicar branding
    const branding = await brandingService.loadAndApplyBranding(effectiveNegocioId);

    // Dispatch evento personalizado para que otros componentes sepan que el branding está listo
    const brandingReadyEvent = new CustomEvent("brandingReady", {
      detail: { branding },
    });
    window.dispatchEvent(brandingReadyEvent);

    return branding;
  } catch (error) {
    console.error("Failed to initialize branding:", error);
    throw error;
  }
};

/**
 * Hook personalizado para escuchar cuando el branding esté listo
 * @param {Function} callback - Función a ejecutar cuando el branding esté listo
 */
export const onBrandingReady = (callback) => {
  const handleBrandingReady = (event) => {
    callback(event.detail.branding);
  };

  window.addEventListener("brandingReady", handleBrandingReady);

  // Retornar función de cleanup
  return () => {
    window.removeEventListener("brandingReady", handleBrandingReady);
  };
};

/**
 * Actualiza el branding cuando cambia el negocio
 * @param {number} negocioId - ID del nuevo negocio
 * @returns {Promise<Object>} Nueva configuración de branding
 */
export const updateBrandingForNegocio = async (negocioId) => {
  try {
    console.log(`🔄 Updating branding for negocio: ${negocioId}`);

    // Si hay WHITE_LABEL_ID configurado, no actualizar por negocio
    if (WHITE_LABEL_ID) {
      console.log("⚠️ WHITE_LABEL_ID is configured, skipping negocio-based branding update");
      return brandingService.currentBranding;
    }

    const branding = await brandingService.refreshBranding(negocioId);

    // Dispatch evento de actualización
    const brandingUpdatedEvent = new CustomEvent("brandingUpdated", {
      detail: { branding, negocioId },
    });
    window.dispatchEvent(brandingUpdatedEvent);

    return branding;
  } catch (error) {
    console.error("Failed to update branding for negocio:", error);
    throw error;
  }
};

const brandingUtils = {
  initializeBranding,
  onBrandingReady,
  updateBrandingForNegocio,
};

export default brandingUtils;
